import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Wifi, Zap, Globe, TrendingUp, ArrowRight, Play } from 'lucide-react';

const AnimatedHero = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = 600;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        // Multi-color particles: Red, Pink, Blue, Gold
        const colors = [
          'rgba(239, 68, 68, 0.6)',   // Red
          'rgba(236, 72, 153, 0.6)',  // Pink
          'rgba(59, 130, 246, 0.6)',  // Blue
          'rgba(251, 191, 36, 0.6)'   // Gold
        ];
        ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
        ctx.fill();
      }
    }

    const particles = Array.from({ length: 150 }, () => new Particle());

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((particle, i) => {
        particle.update();
        particle.draw();

        // Draw connections
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            // Multi-color connections
            const connectionColors = [
              `rgba(239, 68, 68, ${0.3 * (1 - distance / 100)})`,   // Red
              `rgba(236, 72, 153, ${0.3 * (1 - distance / 100)})`,  // Pink
              `rgba(59, 130, 246, ${0.3 * (1 - distance / 100)})`,  // Blue
              `rgba(251, 191, 36, ${0.3 * (1 - distance / 100)})`   // Gold
            ];
            ctx.strokeStyle = connectionColors[Math.floor(Math.random() * connectionColors.length)];
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = 600;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="relative min-h-[450px] bg-gradient-to-br from-red-900 via-pink-900 to-blue-900 overflow-hidden">
      {/* Animated Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />

      {/* Animated Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 via-pink-900/40 to-blue-900/40 animate-gradient-shift" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Content - Compact */}
          <div className="text-white space-y-5 animate-fade-in text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500/20 to-pink-500/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-red-400/30 animate-pulse-slow">
              <Zap className="h-4 w-4 text-amber-400 animate-pulse" />
              <span className="text-xs font-bold bg-gradient-to-r from-red-200 to-pink-200 bg-clip-text text-transparent">India's Fastest Growing ISP</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-black leading-tight">
              <span className="animate-text-shimmer bg-gradient-to-r from-red-400 via-pink-400 to-red-400 bg-clip-text text-transparent bg-[length:200%_auto]">
                Lightning Fast
              </span>
              <span className="block animate-text-shimmer bg-gradient-to-r from-pink-400 via-blue-400 to-amber-400 bg-clip-text text-transparent bg-[length:200%_auto]" style={{ animationDelay: '0.5s' }}>
                Internet Speed
              </span>
            </h1>

            <p className="text-base text-pink-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Experience blazing-fast fiber optic internet with speeds up to 1 Gbps. 
              Connect with confidence, stream without limits, and work from anywhere.
            </p>

            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <Link to="/signup">
                <Button
                  className="bg-gradient-to-r from-red-600 via-pink-600 to-red-600 hover:from-red-700 hover:to-pink-700 text-white font-black px-6 py-3 text-base shadow-xl hover:shadow-red-500/50 transition-all animate-pulse-glow border-2 border-red-400/50"
                >
                  Get Started Now
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>

              <Link to="/speed-test">
                <Button
                  variant="outline"
                  className="border-2 border-amber-400/50 text-white hover:bg-amber-500/20 px-6 py-3 text-base backdrop-blur-sm font-bold hover:border-amber-400 transition-all"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Test Your Speed
                </Button>
              </Link>
            </div>

            {/* Stats with Live Running Animation */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gradient-to-r from-red-400/30 via-pink-400/30 to-blue-400/30">
              <div className="group">
                <div className="text-3xl font-black bg-gradient-to-r from-red-400 to-pink-500 bg-clip-text text-transparent group-hover:scale-110 transition-transform">1Gbps</div>
                <div className="text-sm text-pink-200 font-semibold">Max Speed</div>
              </div>
              <div className="group">
                <div className="text-3xl font-black bg-gradient-to-r from-pink-400 to-blue-500 bg-clip-text text-transparent group-hover:scale-110 transition-transform">99.9%</div>
                <div className="text-sm text-pink-200 font-semibold">Uptime</div>
              </div>
              <div className="group">
                <div className="text-3xl font-black bg-gradient-to-r from-blue-400 to-amber-500 bg-clip-text text-transparent group-hover:scale-110 transition-transform">24/7</div>
                <div className="text-sm text-pink-200 font-semibold">Support</div>
              </div>
            </div>
          </div>

          {/* Right Content - Floating Cards with New Colors */}
          <div className="relative h-[500px] hidden lg:block">
            <div className="absolute inset-0">
              {/* Floating Card 1 - Red */}
              <div
                className="absolute top-0 right-0 w-64 bg-gradient-to-br from-red-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-6 shadow-2xl border-2 border-red-400/30 animate-float hover:scale-105 transition-transform"
                style={{ animationDelay: '0s' }}
              >
                <div className="bg-gradient-to-br from-red-500 to-pink-600 p-3 rounded-xl w-fit mb-4">
                  <Wifi className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-white font-black text-lg mb-2">Fiber Optic</h3>
                <p className="text-pink-100 text-sm">Ultra-fast and reliable connectivity</p>
              </div>

              {/* Floating Card 2 - Pink/Blue */}
              <div
                className="absolute top-32 left-0 w-64 bg-gradient-to-br from-pink-500/20 to-blue-500/20 backdrop-blur-md rounded-2xl p-6 shadow-2xl border-2 border-pink-400/30 animate-float hover:scale-105 transition-transform"
                style={{ animationDelay: '1s' }}
              >
                <div className="bg-gradient-to-br from-pink-500 to-blue-600 p-3 rounded-xl w-fit mb-4">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-white font-black text-lg mb-2">Wide Coverage</h3>
                <p className="text-pink-100 text-sm">Available across major cities in India</p>
              </div>

              {/* Floating Card 3 - Blue/Gold */}
              <div
                className="absolute bottom-0 right-20 w-64 bg-gradient-to-br from-blue-500/20 to-amber-500/20 backdrop-blur-md rounded-2xl p-6 shadow-2xl border-2 border-amber-400/30 animate-float hover:scale-105 transition-transform"
                style={{ animationDelay: '2s' }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-amber-600 p-3 rounded-xl w-fit mb-4">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-white font-black text-lg mb-2">Best Prices</h3>
                <p className="text-pink-100 text-sm">Affordable plans for everyone</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scan-horizontal {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }

        .animate-scan-horizontal {
          animation: scan-horizontal 4s linear infinite;
        }

        @keyframes gradient-shift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }

        @keyframes text-shimmer {
          0% {
            background-position: 0% center;
          }
          100% {
            background-position: 200% center;
          }
        }

        .animate-text-shimmer {
          animation: text-shimmer 3s linear infinite;
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(239, 68, 68, 0.5);
          }
          50% {
            box-shadow: 0 0 40px rgba(236, 72, 153, 0.8), 0 0 60px rgba(236, 72, 153, 0.5);
          }
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AnimatedHero;
